<script setup lang="ts">
import moment from "moment";
import { computed } from "vue";

import DatePicker from "~/components/dumb/DatePicker.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { RecurrenceIcon } from "~/icons";
import { EditorMode, ViewKind } from "~/shared/enums";
import type { Recurrence, Task } from "~/shared/types";
import { useAppStore, usePageStore } from "~/stores";
import { getRecurrenceSummary } from "~/utils/time";

const props = defineProps<{
  editorMode: EditorMode;
  task: Task;
  recurrence: Recurrence | null;
  recursNextAt: string | null;
  containerClasses?: string;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);

const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);

const hasRecurrence = computed(() => !!props.recurrence && !!props.recursNextAt);

const recurrenceSummary = computed(() => {
  if (!props.recurrence || !props.recursNextAt) {
    return "";
  }
  const summary = getRecurrenceSummary(props.recurrence, moment(props.recursNextAt));
  return isBoardMode.value || isDetailMode.value ? summary.replace(/^./, (e) => e.toUpperCase()) : summary;
});
</script>

<template>
  <DatePicker
    :value="task.recursNextAt"
    :editor-mode="editorMode"
    :tasks="[task]"
    :recurrence="task.recurrence"
    :recurs-next-at="task.recursNextAt"
    is-recurrence
    cover
    :class="containerClasses"
    :on-after-open="onAfterOpen"
    :on-after-close="onAfterClose"
    :disabled="isBoardMode || pageStore.isPublicView || isTrash">
    <Tooltip :text="isBoardMode ? 'Recurrence' : 'Change recurrence'" class="w-full">
      <button
        v-if="hasRecurrence"
        type="button"
        class="flex items-center rounded-sm focus-ring-none"
        :class="[
          isBoardMode && 'text-md',
          isDetailMode
            ? 'w-full justify-start gap-2 px-2 py-1 text-md hover:bg-lt'
            : 'h-5 justify-center gap-1 border border-oncolor px-1 text-vlt hover:bg-opposite/10',
        ]"
        tabindex="-1">
        <span class="sr-only">Recurrence settings</span>
        <RecurrenceIcon class="icon-xs" />
        <span class="text-md" :class="isDetailMode ? 'text-left text-sm' : 'text-xs'">
          {{ recurrenceSummary }}
        </span>
      </button>
    </Tooltip>
  </DatePicker>
</template>
